import React, { useEffect, useMemo, useState } from 'react'
import { Empty, Input, Spin, Tag, Tree, Typography } from 'antd'
import { useLazyQuery } from '@apollo/client'
import { GET_DOMAINS } from '../gql/registroHorasGQLignore'
import { DOMAIN_CONFIG } from '../shared/domainConfig'
import { buildDomainLists } from '../shared/buildDomainLists'
import type { ActivityGuideTreeNode } from '../shared/activityGuideTree'
import { buildActivityGuideTree, filterActivityGuideTree } from '../shared/activityGuideTree'

const { Search } = Input
const { Text } = Typography
const DOMAINS_QUERY = GET_DOMAINS()

interface ActivityReferenceTableProps {
  scrollY?: number | string
}

function highlightMatch(text: string, rawSearchTerm: string) {
  const searchTerm = rawSearchTerm.trim()
  if (!searchTerm) return text

  const normalizedText = text.toLocaleLowerCase()
  const normalizedSearch = searchTerm.toLocaleLowerCase()
  const matchIndex = normalizedText.indexOf(normalizedSearch)

  if (matchIndex === -1) {
    return text
  }

  const before = text.slice(0, matchIndex)
  const match = text.slice(matchIndex, matchIndex + searchTerm.length)
  const after = text.slice(matchIndex + searchTerm.length)

  return (
    <span>
      {before}
      <mark style={{ backgroundColor: '#fff3bf', padding: 0 }}>{match}</mark>
      {after}
    </span>
  )
}

function renderActivityNode(node: ActivityGuideTreeNode, searchTerm: string) {
  const labels = {
    general: 'General',
    objective: 'Objetivo',
    specific: 'Específica',
  }

  const colors = {
    general: 'green',
    objective: 'blue',
    specific: 'default',
  } as const

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Tag color={colors[node.level]} style={{ marginRight: 0, minWidth: 72, textAlign: 'center' }}>
        {labels[node.level]}
      </Tag>
      <Text strong={node.level !== 'specific'}>
        {highlightMatch(String(node.title ?? ''), searchTerm)}
      </Text>
    </span>
  )
}

const ActivityReferenceTable: React.FC<ActivityReferenceTableProps> = ({
  scrollY = 480,
}) => {
  const [loadDomains, { data: domainsData, loading }] = useLazyQuery(DOMAINS_QUERY, {
    fetchPolicy: 'no-cache',
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])
  const [autoExpandParent, setAutoExpandParent] = useState(true)

  useEffect(() => {
    loadDomains()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const treeData = useMemo<ActivityGuideTreeNode[]>(() => {
    if (!domainsData) return []
    return buildActivityGuideTree(buildDomainLists(domainsData, DOMAIN_CONFIG))
  }, [domainsData])

  const { filteredTree, expandedKeys: matchedExpandedKeys } = useMemo(
    () => filterActivityGuideTree(treeData, searchTerm),
    [treeData, searchTerm]
  )

  useEffect(() => {
    setExpandedKeys(matchedExpandedKeys)
    setAutoExpandParent(true)
  }, [matchedExpandedKeys])

  if (loading) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      <Search
        allowClear
        placeholder='Buscar actividad, objetivo o actividad específica'
        value={searchTerm}
        onChange={event => setSearchTerm(event.target.value)}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8 }}>
        {filteredTree.length > 0 ? (
          <Tree
            showLine
            blockNode
            treeData={filteredTree}
            expandedKeys={expandedKeys}
            autoExpandParent={autoExpandParent}
            onExpand={keys => {
              setExpandedKeys(keys)
              setAutoExpandParent(false)
            }}
            titleRender={node => renderActivityNode(node as ActivityGuideTreeNode, searchTerm)}
            height={typeof scrollY === 'number' ? scrollY : undefined}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No se encontraron actividades' />
        )}
      </div>
    </div>
  )
}

export default ActivityReferenceTable
